import type { DashboardCardPref, UserPrefs } from "@strat/api";

/**
 * The cards the Dashboard can show, in their default order, one under the
 * other at full width. The page draws them; this list is what Settings →
 * Dashboard and the page's Customize mode arrange. (Saved backtests and the
 * bots you follow live on the Backtests and Bots tabs.)
 */
export const DASHBOARD_CARDS = [
  { id: "strategies", title: "My strategies" },
  { id: "backtests", title: "Recent backtests" },
  { id: "bots", title: "My bots" },
] as const;

export type DashboardCardId = (typeof DASHBOARD_CARDS)[number]["id"];

/**
 * The saved arrangement, made complete: cards in the saved order, then any
 * card the person has never arranged (one added since) in its default place,
 * visible. Ids that no longer exist are dropped.
 */
export function arrangeCards(
  saved: readonly DashboardCardPref[],
): Array<{ id: DashboardCardId; visible: boolean }> {
  const known = new Set<string>(DASHBOARD_CARDS.map((c) => c.id));
  const out = saved
    .filter((c) => known.has(c.id))
    .map((c) => ({ id: c.id as DashboardCardId, visible: c.visible }));
  for (const card of DASHBOARD_CARDS)
    if (!out.some((c) => c.id === card.id)) out.push({ id: card.id, visible: true });
  return out;
}

/** Moves a card one place up or down. */
export function moveCard(prefs: UserPrefs, id: DashboardCardId, direction: -1 | 1): UserPrefs {
  const cards = arrangeCards(prefs.dashboard.cards);
  const at = cards.findIndex((c) => c.id === id);
  const other = at + direction;
  if (at < 0 || other < 0 || other >= cards.length) return prefs;
  [cards[at], cards[other]] = [cards[other]!, cards[at]!];
  return { ...prefs, dashboard: { cards } };
}

export function setCardVisible(prefs: UserPrefs, id: DashboardCardId, visible: boolean): UserPrefs {
  const cards = arrangeCards(prefs.dashboard.cards).map((c) => (c.id === id ? { ...c, visible } : c));
  return { ...prefs, dashboard: { cards } };
}
